import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { env } from '@env';

/**
 * PERSISTENT LOGGING for deep link debugging
 * Logs are stored in sessionStorage and survive redirects
 */
const persistLog = (message: string, data?: any) => {
  const logs = JSON.parse(sessionStorage.getItem('deepLinkLogs') || '[]');
  logs.push({
    timestamp: new Date().toISOString(),
    message,
    data: data ? JSON.stringify(data) : undefined,
  });
  sessionStorage.setItem('deepLinkLogs', JSON.stringify(logs));
  console.log(`[PERSISTENT] ${message}`, data || '');
};

// Expose log viewer globally
(window as any).viewDeepLinkLogs = () => {
  const logs = JSON.parse(sessionStorage.getItem('deepLinkLogs') || '[]');
  console.table(logs);
  return logs;
};

(window as any).clearDeepLinkLogs = () => {
  sessionStorage.removeItem('deepLinkLogs');
  console.log('Logs cleared');
};

/**
 * CRITICAL: Detect deep link URL at the EARLIEST possible moment
 * Before React loads, before any auth checks
 */
const currentUrl = window.location.href;
const currentPath = window.location.pathname;
const currentSearch = window.location.search;

persistLog('🚀 App loading', {
  href: currentUrl,
  pathname: currentPath,
  search: currentSearch.substring(0, 50) + '...',
});

// CRITICAL: If this is a deep link with token, set flag IMMEDIATELY
// This prevents MobileTokenManager from redirecting to login
const isDeepLinkWithToken = currentPath.startsWith('/app/action/') &&
  currentSearch.includes('token=');
const isAnyDeepLink = currentPath.startsWith('/app/action/');

if (isDeepLinkWithToken) {
  localStorage.setItem('processingToken', 'true');
  localStorage.setItem('deepLinkUrl', currentPath + currentSearch);
  persistLog('🔑 Deep link with token detected - set processingToken=true', { path: currentPath });
} else if (isAnyDeepLink) {
  localStorage.setItem('deepLinkUrl', currentPath + currentSearch);
  persistLog('🔗 Deep link detected (no token)', { path: currentPath });
}

/**
 * CRITICAL: Register and expose Capacitor plugins globally
 * This ensures MFEs can access the native implementations
 */
// import { registerPlugin, Capacitor } from '@capacitor/core';

// // Plugin interfaces
// interface SecureStoragePluginInterface {
//   get(options: { key: string }): Promise<{ value: string }>;
//   set(options: { key: string; value: string }): Promise<void>;
//   remove(options: { key: string }): Promise<void>;
//   clear(): Promise<void>;
//   keys(): Promise<{ value: string[] }>;
//   getPlatform(): Promise<{ value: string }>;
// }

// interface DeviceSecurityDetectInterface {
//   isJailBreakOrRooted(): Promise<{ value: boolean }>;
//   isDeviceSecure(): Promise<{ value: boolean }>;
//   isDebugMode(): Promise<{ value: boolean }>;
// }

// interface NetworkPluginInterface {
//   getStatus(): Promise<{ connected: boolean; connectionType: string }>;
//   addListener(eventName: string, listenerFunc: (info: any) => void): Promise<{
//     remove: () => void
//   }>;
// }

// CRITICAL: Register plugins after DOM is ready to ensure Capacitor bridge is initialized
// In iOS, the bridge needs to be fully initialized before plugins can be registered
// let SecureStoragePlugin: SecureStoragePluginInterface;
// let DeviceSecurityDetect: DeviceSecurityDetectInterface;
// let NetworkPlugin: NetworkPluginInterface;

// function registerCapacitorPlugins() {
//   SecureStoragePlugin = registerPlugin<SecureStoragePluginInterface>('SecureStoragePlugin');
//   DeviceSecurityDetect = registerPlugin<DeviceSecurityDetectInterface>('DeviceSecurityDetect');
//   NetworkPlugin = registerPlugin<NetworkPluginInterface>('Network');

//   if (Capacitor.isNativePlatform()) {
//     // Small delay to ensure bridge is ready
//     setTimeout(() => {
//       NetworkPlugin.getStatus()
//         .then((result) => {
//           console.log('✅ NetworkPlugin works! Status:', result);
//         })
//         .catch((error) => {
//           console.error('❌ NetworkPlugin failed:', error);
//           console.error('   Error code:', (error as any).code);
//           console.error('   Error message:', (error as any).message);
//           console.error('   Full error:', JSON.stringify(error, null, 2));
//         });
//     }, 500);
//   }

//   // Test if plugins actually work (only in native platforms)
//   if (Capacitor.isNativePlatform()) {
//     SecureStoragePlugin.getPlatform()
//       .then((result) => {
//         console.log('✅ SecureStoragePlugin works! Platform:', result.value);
//       })
//       .catch((error) => {
//         console.error('❌ SecureStoragePlugin failed:', error);
//         console.error('   Error details:', JSON.stringify(error, null, 2));
//       });
//   }

//   // Expose plugins globally for MFEs to consume
//   if (typeof window !== 'undefined') {
//     (window as any).AllieCapacitorPlugins = {
//       SecureStoragePlugin,
//       DeviceSecurityDetect,
//       Network: NetworkPlugin,
//     };
//   }
// }

// // Register plugins when DOM is ready
// if (document.readyState === 'loading') {
//   document.addEventListener('DOMContentLoaded', registerCapacitorPlugins);
// } else {
//   // DOM already loaded, but wait a bit for Capacitor bridge to initialize
//   setTimeout(registerCapacitorPlugins, 100);
// }

/**
 * Minimal entry point for the shell application
 * All complex logic (contexts, service workers, etc.) is now handled by app-provider
 */
const ENV_SUFFIX = env.REACT_APP_TEST_ENV ? '-test' : '';

document.querySelector('#manifest-placeholder')?.setAttribute('href', `manifest${ENV_SUFFIX}.json`);
document
  .querySelector('#favicon-placeholder')
  ?.setAttribute('href', `assets/icon/favicon${ENV_SUFFIX}.png`);

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Initialize deep link handler AFTER React app is mounted and ONLY in native
// This prevents interference with platform detection during app initialization
if ((window as any).Capacitor?.isNativePlatform()) {
  // Dynamic import to ensure it only loads in native and after app mount
  import('./deepLinkHandler').then(({ initializeDeepLinkHandler }) => {
    initializeDeepLinkHandler();
  });
}
